/**
 * Pipeline runner UI — drives state-machine progression from `running`
 * through the optional STL/LSTM threshold pauses to `complete`.
 *
 * Owns the modal mount/unmount; PipelineRunner sits next to the parameter
 * form on the page and reacts to the FSM state owned by `useAnomalyPipeline`.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { mapAdjustmentsBackendError } from '@/features/data-adjustments/utils/adjustmentsBackendErrorMapper';
import { Lang, PipelineStatus } from '../types';
import { UseAnomalyPipeline } from '../useAnomalyPipeline';
import ThresholdInputModal from './ThresholdInputModal';

interface Props {
  pipeline: UseAnomalyPipeline;
  lang: Lang;
}

// Statuses during which ThresholdInputModal already surfaces the error via its
// serverError prop — showing the banner too would duplicate the message.
const BANNER_SUPPRESSED_STATUSES: readonly PipelineStatus[] = [
  'awaiting_stl_threshold',
  'awaiting_lstm_threshold',
  'applying_stl',
  'applying_lstm',
];

const PipelineRunner: React.FC<Props> = ({ pipeline, lang }) => {
  const { state, submitStlThreshold, submitLstmThreshold } = pipeline;
  const { t } = useTranslation('dataProcessing');

  // Pre-flight banner — surfaces engine-side errors that aren't tied to a modal.
  // Suppressed during awaiting/applying states because ThresholdInputModal already
  // renders the same message via its serverError prop; showing it twice is confusing.
  //
  // Translates via mapAdjustmentsBackendError at render time (matches the top-level
  // error rendering in DataProcessingPage) so language switches re-translate.
  const bannerText = (() => {
    if (!state.errorMessage && !state.errorCode) return null;
    const mapped = mapAdjustmentsBackendError({
      code: state.errorCode ?? undefined,
      message: state.errorMessage ?? undefined,
      suggestion: state.errorSuggestion ?? undefined,
    });
    if (mapped.translationKey) {
      return t(`dataAdjustments:${mapped.translationKey}`, {
        defaultValue: mapped.fallbackMessage,
      });
    }
    return mapped.fallbackMessage || state.errorMessage || null;
  })();

  const banner =
    bannerText && !BANNER_SUPPRESSED_STATUSES.includes(state.status) ? (
      <div role="alert" className="my-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
        {bannerText}
      </div>
    ) : null;

  const isNanLstmError = !!state.errorMessage &&
    /kann nicht durchgeführt werden, weil NaN|cannot be performed because the dataset contains NaN/i.test(state.errorMessage);

  const nanHint = isNanLstmError ? (
    <div role="alert" className="my-3 rounded border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900" data-testid="nan-lstm-hint">
      <p className="font-medium mb-2">{t('errors.nanInDatasetHint')}</p>
      <ul className="list-disc list-inside space-y-1">
        <li>{t('errors.nanInDatasetSuggestions.raiseThreshold')}</li>
        <li>{t('errors.nanInDatasetSuggestions.raiseGapMax')}</li>
        <li>{t('errors.nanInDatasetSuggestions.disableLstm')}</li>
      </ul>
    </div>
  ) : null;

  if (state.status === 'awaiting_stl_threshold' || state.status === 'applying_stl') {
    const plots = state.plots.stlDecomposition ?? [];
    return (
      <>
        {banner}
        {nanHint}
        {/* Informational (not an error): STL succeeded but skipped runs shorter
            than 2 periods, so part of the data was not scanned. Rendered INSIDE
            the modal so the user sees it while choosing the threshold. The text
            is already localized by the backend — shown verbatim so locales never
            drift. */}
        <ThresholdInputModal
          variant="stl"
          plots={plots}
          notice={state.stlCoverage?.warning ?? null}
          serverError={state.errorMessage}
          submitting={state.status === 'applying_stl'}
          onSubmit={(v) => submitStlThreshold(v, lang)}
          onCancel={() => void pipeline.cancel()}
        />
      </>
    );
  }

  if (state.status === 'awaiting_lstm_threshold' || state.status === 'applying_lstm') {
    const plots = state.plots.lstmError ? [state.plots.lstmError] : [];
    return (
      <>
        {banner}
        {nanHint}
        <ThresholdInputModal
          variant="lstm"
          plots={plots}
          serverError={state.errorMessage}
          submitting={state.status === 'applying_lstm'}
          onSubmit={(v) => submitLstmThreshold(v, lang)}
          onCancel={() => void pipeline.cancel()}
        />
      </>
    );
  }

  return (
    <>
      {banner}
      {nanHint}
    </>
  );
};

export default PipelineRunner;
